import Keycloak, { KeycloakInitOptions } from 'keycloak-js';
import { BehaviorSubject, Observable } from 'rxjs';

import { inject, Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';

/** Folga usada para renovar o token antes de ele vencer. */
const TOKEN_MIN_VALIDITY_SECONDS = 30;

/**
 * Quanto o app espera o keycloak.init() antes de seguir como deslogado. O
 * keycloak-js ja usa 10 s para o iframe de cookies de terceiros, mas o iframe
 * do silent SSO nao tem limite nenhum: com o Keycloak travado, quem espera o
 * init esperaria para sempre.
 */
export const INIT_TIMEOUT_MS = 10000;

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private readonly keycloak = inject(Keycloak);

  // Comeca true: sem iniciar() nao ha init para esperar. Comecando false, uma
  // requisicao que chegasse ao interceptor antes do iniciar() esperaria a
  // promise ja resolvida, reentraria e esperaria de novo, num laco de microtasks
  // que trava a aba.
  private _inicializado = true;
  private _pronto: Promise<void> = Promise.resolve();
  private readonly _logado = new BehaviorSubject<boolean>(false);

  /** Estado da sessao para template: muda quando o keycloak.init() termina de verdade. */
  readonly logado$: Observable<boolean> = this._logado.asObservable();

  /**
   * Chama o keycloak.init(). A promise resolve quando o init termina ou quando
   * passa INIT_TIMEOUT_MS, e nunca rejeita: com o Keycloak fora do ar o app
   * segue deslogado em vez de ficar em branco. logado$ acompanha o init real,
   * mesmo que ele termine depois do timeout.
   */
  iniciar(opcoes: KeycloakInitOptions): Promise<void> {
    this._inicializado = false;
    let limite: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>(resolve => {
      limite = setTimeout(() => {
        console.error(`Keycloak nao respondeu em ${INIT_TIMEOUT_MS / 1000} s; seguindo sem sessao.`);
        resolve();
      }, INIT_TIMEOUT_MS);
    });

    const init = this.keycloak.init(opcoes).then(
      () => undefined,
      error => console.error('Keycloak initialization failed', error)
    ).finally(() => {
      clearTimeout(limite);
      this._logado.next(this.isLoggedIn());
    });

    this._pronto = Promise.race([init, timeout]).then(() => {
      this._inicializado = true;
    });
    return this._pronto;
  }

  /**
   * O que o app initializer chama. O init sempre comeca, mas o bootstrap so o
   * espera na volta do login: o check-sso custa dois iframes no Keycloak (~1 s
   * no Slow 4G, medido em 2026-09-22) e a landing publica nao precisa de
   * sessao, entao o app sobe na hora e quem precisa espera o init (AuthGuard e
   * o interceptor). Na volta do login o bootstrap segura como antes, para o
   * keycloak-js ler e trocar o code antes de o router mexer na URL.
   *
   * Fica aqui e nao no app.config.ts porque la nao tem spec: o ng test compila
   * o environment.ts vazio, e o provideKeycloak faz new Keycloak({ url: '' })
   * ao importar o arquivo, que lanca antes de o TestBed poder trocar qualquer coisa.
   */
  iniciarNoBootstrap(opcoes: KeycloakInitOptions): Promise<void> | undefined {
    const init = this.iniciar(opcoes);
    return this.voltouDoLogin() ? init : undefined;
  }

  /** Resolve quando o init terminou (ou desistiu). Antes de iniciar(), ja resolvida. */
  aguardarInit(): Promise<void> {
    return this._pronto;
  }

  /**
   * Sincrono: o interceptor so paga a espera enquanto o init roda. False so
   * entre o iniciar() e o fim do init (ou do timeout).
   */
  inicializado(): boolean {
    return this._inicializado;
  }

  /**
   * A URL traz a volta do login (state + code, ou state + error)? Nesse caso o
   * bootstrap espera o init, para o keycloak-js ler e trocar o code antes de o
   * router mexer na URL. Olha fragmento e query: responseMode e 'fragment' hoje,
   * e trocar para 'query' nao pode desligar a espera em silencio.
   */
  voltouDoLogin(href: string = window.location.href): boolean {
    const url = new URL(href);
    const params = new URLSearchParams(`${url.hash.slice(1)}&${url.search.slice(1)}`);
    return params.has('state') && (params.has('code') || params.has('error'));
  }

  redirectToLoginPage(): Promise<void> {
    return this.keycloak.login({ redirectUri: environment.keycloak.redirectUri });
  }

  isLoggedIn(): boolean {
    return !!this.keycloak.authenticated;
  }

  getToken(): string | null {
    return this.keycloak.token ?? null;
  }

  /**
   * O token vence nos proximos TOKEN_MIN_VALIDITY_SECONDS? Sincrono, para o
   * interceptor so pagar o refresh quando ele e necessario.
   */
  needsTokenRefresh(): boolean {
    return this.isTokenExpired(TOKEN_MIN_VALIDITY_SECONDS);
  }

  async refreshTokenIfNeeded(): Promise<void> {
    // Renova ANTES de expirar. O guard antigo era isTokenExpired() sem folga,
    // entao so entrava aqui com o token ja vencido e a folga de 30s do
    // updateToken nunca valia de nada.
    if (this.needsTokenRefresh()) {
      try {
        await this.keycloak.updateToken(TOKEN_MIN_VALIDITY_SECONDS);
      } catch (error) {
        console.error('Error refreshing token', error);
        throw error;
      }
    }
  }

  logout(): void {
    this.keycloak.logout({ redirectUri: environment.keycloak.postLogoutRedirectUri });
  }

  /**
   * Delega ao keycloak-js porque ele aplica o timeSkew medido no init: contar
   * com Date.now() cru erra nos dois sentidos quando o relogio do cliente esta
   * torto. `minValidity` responde "expira nos proximos N segundos?".
   */
  isTokenExpired(minValidity?: number): boolean {
    try {
      return this.keycloak.isTokenExpired(minValidity);
    } catch {
      // keycloak-js lanca quando nao ha token; sem token, tratamos como expirado.
      return true;
    }
  }
}